import { useEffect, useRef, useState } from "react";
import { Camera, RefreshCw, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { buildWatermarkLines, LatLng } from "@/features/driver/lib/driverPresentation.js";
import { useDriverLanguage } from "@/features/driver/i18n/useDriverLanguage";

interface CameraCaptureProps {
  order: { customer_address?: string };
  title?: string;
  onCapture: (dataUrl: string, coords: LatLng | null) => void;
  onCancel: () => void;
}

/**
 * Live device camera capture with a GPS + timestamp watermark burned onto the
 * image via Canvas. The watermark text is produced by the tested
 * buildWatermarkLines helper. Falls back gracefully when camera or geolocation
 * permissions are unavailable.
 */
export default function CameraCapture({ order, title, onCapture, onCancel }: CameraCaptureProps) {
  const { t } = useDriverLanguage();
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [coords, setCoords] = useState<LatLng | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function start() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => undefined);
        }
      } catch {
        setError(t("cameraUnavailable"));
      }
    }

    start();

    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => setCoords({ lat: position.coords.latitude, lng: position.coords.longitude }),
        () => setCoords(null),
        { enableHighAccuracy: true, timeout: 10000 },
      );
    }

    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    };
  }, []);

  const stopStream = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  };

  const capture = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) {
      return;
    }

    const width = video.videoWidth || 640;
    const height = video.videoHeight || 480;
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }

    ctx.drawImage(video, 0, 0, width, height);

    // Watermark: semi-transparent band + tested text lines.
    const lines = buildWatermarkLines(order, coords, new Date());
    const fontSize = Math.max(14, Math.round(width / 36));
    const lineHeight = fontSize * 1.4;
    const bandHeight = lineHeight * lines.length + lineHeight * 0.6;

    ctx.fillStyle = "rgba(0, 0, 0, 0.55)";
    ctx.fillRect(0, height - bandHeight, width, bandHeight);
    ctx.fillStyle = "#ffffff";
    ctx.font = `${fontSize}px Arial, sans-serif`;
    ctx.textBaseline = "top";
    lines.forEach((line, index) => {
      ctx.fillText(line, 12, height - bandHeight + lineHeight * 0.3 + index * lineHeight);
    });

    setPreview(canvas.toDataURL("image/jpeg", 0.85));
  };

  const confirm = () => {
    if (preview) {
      stopStream();
      onCapture(preview, coords);
    }
  };

  const retake = () => setPreview(null);

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black/90 p-4">
      <div className="mx-auto flex w-full max-w-lg items-center justify-between text-white">
        <h2 className="text-lg font-semibold">{title ?? t("arrivalPhotoTitle")}</h2>
        <button type="button" onClick={() => { stopStream(); onCancel(); }} aria-label={t("closeCameraAria")}>
          <X className="h-6 w-6" />
        </button>
      </div>

      <div className="mx-auto mt-3 w-full max-w-lg overflow-hidden rounded-md bg-black">
        {error ? (
          <p className="p-6 text-center text-sm text-red-300">{error}</p>
        ) : preview ? (
          <img src={preview} alt={t("capturedProofAlt")} className="w-full" />
        ) : (
          <video ref={videoRef} playsInline muted className="w-full" />
        )}
        <canvas ref={canvasRef} className="hidden" />
      </div>

      <div className="mx-auto mt-4 flex w-full max-w-lg gap-3">
        {preview ? (
          <>
            <Button type="button" variant="secondary" className="flex-1" onClick={retake}>
              <RefreshCw className="mr-2 h-4 w-4" /> {t("retakeBtn")}
            </Button>
            <Button type="button" className="flex-1" onClick={confirm}>
              <Check className="mr-2 h-4 w-4" /> {t("usePhotoBtn")}
            </Button>
          </>
        ) : (
          <Button type="button" className="flex-1" disabled={Boolean(error)} onClick={capture}>
            <Camera className="mr-2 h-4 w-4" /> {t("captureBtn")}
          </Button>
        )}
      </div>
    </div>
  );
}
